import { createContext, useContext, useReducer, ReactNode } from 'react';
import { CTCResult, ValidationResult } from '../types';
import { calculateCTC } from '../utils/ctcCalculator';
import { validateSalary } from '../utils/validators';
import { useConfig } from './ConfigContext';
import { getCustomRates, CustomRates, SalaryTypeKey } from '../services/customRatesService';

interface CalculatorState {
  baseSalary: number;
  connectivityAllowance: number;
  salaryType: 'ordinario_inf' | 'ordinario_sup' | 'integral' | 'auto';
  riskLevel: number;
  selectedOperativeCosts: string[]; // Array de nombres de costos operativos seleccionados
  result: CTCResult | null;
  validation: ValidationResult;
  isCalculating: boolean;
}

type CalculatorAction =
  | { type: 'SET_SALARY'; payload: number }
  | { type: 'SET_CONNECTIVITY'; payload: number }
  | { type: 'SET_SALARY_TYPE'; payload: 'ordinario_inf' | 'ordinario_sup' | 'integral' | 'auto' }
  | { type: 'SET_RISK_LEVEL'; payload: number }
  | { type: 'SET_OPERATIVE_COSTS'; payload: string[] }
  | { type: 'CALCULATE'; payload: CTCResult }
  | { type: 'SET_VALIDATION'; payload: ValidationResult }
  | { type: 'SET_CALCULATING'; payload: boolean }
  | { type: 'CLEAR' };

interface CalculatorContextType {
  state: CalculatorState;
  setSalary: (value: number) => void;
  setConnectivity: (value: number) => void;
  setSalaryType: (value: 'ordinario_inf' | 'ordinario_sup' | 'integral' | 'auto') => void;
  setRiskLevel: (value: number) => void;
  setOperativeCosts: (costs: string[]) => void;
  calculate: () => void;
  clear: () => void;
}

const CalculatorContext = createContext<CalculatorContextType | undefined>(undefined);

function calculatorReducer(state: CalculatorState, action: CalculatorAction): CalculatorState {
  switch (action.type) {
    case 'SET_SALARY':
      return { ...state, baseSalary: action.payload };
    case 'SET_CONNECTIVITY':
      return { ...state, connectivityAllowance: action.payload };
    case 'SET_SALARY_TYPE':
      return { ...state, salaryType: action.payload };
    case 'SET_RISK_LEVEL':
      return { ...state, riskLevel: action.payload };
    case 'SET_OPERATIVE_COSTS':
      return { ...state, selectedOperativeCosts: action.payload };
    case 'CALCULATE':
      return { ...state, result: action.payload, isCalculating: false };
    case 'SET_VALIDATION':
      return { ...state, validation: action.payload };
    case 'SET_CALCULATING':
      return { ...state, isCalculating: action.payload };
    case 'CLEAR':
      return {
        baseSalary: 0,
        connectivityAllowance: 0,
        salaryType: 'auto',
        riskLevel: 0.522,
        selectedOperativeCosts: [],
        result: null,
        validation: { valid: true, errors: [], warnings: [] },
        isCalculating: false,
      };
    default:
      return state;
  }
}

export function CalculatorProvider({ children }: { children: ReactNode }) {
  const { config } = useConfig();
  const [state, dispatch] = useReducer(calculatorReducer, {
    baseSalary: 0,
    connectivityAllowance: 0,
    salaryType: 'auto',
    riskLevel: 0.522,
    selectedOperativeCosts: [],
    result: null,
    validation: { valid: true, errors: [], warnings: [] },
    isCalculating: false,
  });

  const calculate = () => {
    // Validate inputs (but don't block calculation)
    const validation = validateSalary(state.baseSalary, config.smmlv);
    dispatch({ type: 'SET_VALIDATION', payload: validation });

    dispatch({ type: 'SET_CALCULATING', payload: true });

    // Determine effective salary type for getting custom rates
    let effectiveType: SalaryTypeKey;
    if (state.salaryType === 'auto') {
      // Auto-detect based on salary
      const threshold = config.smmlv * 13;
      if (state.baseSalary >= threshold) {
        effectiveType = 'integral';
      } else if (state.baseSalary > config.smmlv * 10) {
        effectiveType = 'ordinario_sup';
      } else {
        effectiveType = 'ordinario_inf';
      }
    } else {
      effectiveType = state.salaryType;
    }

    // Get custom rates for the effective salary type
    const customRates = getCustomRates(effectiveType);

    // Update connectivity allowance if selected
    const connectivityValue = state.selectedOperativeCosts.includes('Connectivity assistance') 
      ? (customRates['Connectivity assistance'] || 0) 
      : 0;

    // Build operative costs object based on selected costs
    const operativeCosts = {
      hiring: state.selectedOperativeCosts.includes('Hiring') ? (customRates['Hiring'] || 0) : 0,
      safetyStudy: state.selectedOperativeCosts.includes('Safety Study') ? (customRates['Safety Study'] || 0) : 0,
      laptopRental: state.selectedOperativeCosts.includes('Laptop Rental') ? (customRates['Laptop Rental'] || 0) : 0,
      deliverySupportCost: state.selectedOperativeCosts.includes('Delivery support cost') ? (customRates['Delivery support cost'] || 0) * 100 : 0,
    };

    const additionalCosts = {
      financing: (customRates['Financing'] || 0) * 100,
      managementFees: (customRates['Management Fees'] || 0) * 100,
    };

    // Perform calculation (always, even with 0 salary to show interface)
    try {
      const result = calculateCTC(
        state.baseSalary, 
        connectivityValue, // Use the value from custom rates if selected
        config,
        state.riskLevel,
        state.salaryType,
        operativeCosts,
        additionalCosts,
        customRates
      );
      dispatch({ type: 'CALCULATE', payload: result });
    } catch (error) {
      console.error('Calculation error:', error);
      dispatch({ type: 'SET_CALCULATING', payload: false });
    }
  };

  const value: CalculatorContextType = {
    state,
    setSalary: (value: number) => dispatch({ type: 'SET_SALARY', payload: value }),
    setConnectivity: (value: number) => dispatch({ type: 'SET_CONNECTIVITY', payload: value }),
    setSalaryType: (value: 'ordinario_inf' | 'ordinario_sup' | 'integral' | 'auto') => 
      dispatch({ type: 'SET_SALARY_TYPE', payload: value }),
    setRiskLevel: (value: number) => dispatch({ type: 'SET_RISK_LEVEL', payload: value }),
    setOperativeCosts: (costs: string[]) => dispatch({ type: 'SET_OPERATIVE_COSTS', payload: costs }),
    calculate,
    clear: () => dispatch({ type: 'CLEAR' }),
  };

  return <CalculatorContext.Provider value={value}>{children}</CalculatorContext.Provider>;
}

export function useCalculator() {
  const context = useContext(CalculatorContext);
  if (context === undefined) {
    throw new Error('useCalculator must be used within a CalculatorProvider');
  }
  return context;
}
